import React, { useState, useEffect } from "react";
import { axiosInstance, config } from "../../Axios/AxiosInstance";
import toast from "react-hot-toast";
import AdminLayout from "./Common/AdminLayout";
import LoadingSkeleton from "./Common/LoadingSkeleton";
import TeamEdit from './TeamEdit';
import { FaInfoCircle, FaBriefcase, FaBuilding, FaList, FaImage, FaUser, FaSave } from 'react-icons/fa';
import "../../asset/new/css/admin-modern.css";

const AboutUsEdit = () => {
  const [aboutUs, setAboutUs] = useState({
    title: "",
    description: "",
    featured_jobs: "",
    psu_jobs: "",
    career_site: "",
    third_party_listings: "",
    sonia_description: "",
    about_us_image: "",
    sonia_image: "",
    illustrate_image: "",
    team: [],
  });
  let header = config;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAboutUs = async () => {
      try {
        const response = await axiosInstance.get("about-us");
        setAboutUs(response?.data?.about || {
          title: "",
          description: "",
          featured_jobs: "",
          psu_jobs: "",
          career_site: "",
          third_party_listings: "",
          sonia_description: "",
          about_us_image: "",
          sonia_image: "",
          illustrate_image: "",
          team: [],
        });
      } catch (err) {
        setError("Failed to fetch About Us data");
        toast.error("Failed to load About Us data");
      } finally {
        setLoading(false);
      }
    };
    fetchAboutUs();
    document.title = "Edit About Us | Admin";
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAboutUs((prevAboutUs) => ({
      ...prevAboutUs,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axiosInstance.post("about-us", aboutUs, header);
      toast.success(response.data.message || "About Us updated successfully!");
    } catch (err) {
      setError("Failed to update About Us data");
      toast.error("Failed to update About Us data");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);
    formData.append("type", type);

    const token = JSON.parse(localStorage.getItem("token"));
    setUploadingImage(type);

    try {
      const response = await axiosInstance.post(`/about-us`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      setAboutUs((prevAboutUs) => ({
        ...prevAboutUs,
        [type]: response?.about?.about_us_image,
      }));
      toast.success("Image uploaded successfully!");
    } catch (err) {
      setError(`Failed to upload ${type} image`);
      toast.error(`Failed to upload image`);
    } finally {
      setUploadingImage(null);
    }
  };

  return (
    <AdminLayout
      title="About Us Settings"
      subtitle="Manage your website's about us page content"
    >
      {loading ? (
        <div className="admin-card">
          <div className="admin-card-body">
            <LoadingSkeleton type="input" count={6} />
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          {/* General Information Card */}
          <div className="admin-card" style={{ marginBottom: 'var(--spacing-xl)' }}>
            <div className="admin-card-header">
              <h3 className="admin-card-title">
                <FaInfoCircle style={{ marginRight: '8px', color: 'var(--admin-primary)' }} />
                General Information
              </h3>
            </div>
            <div className="admin-card-body">
              <div className="admin-form-group">
                <label className="admin-form-label">About Title</label>
                <input
                  type="text"
                  name="title"
                  value={aboutUs.title}
                  onChange={handleChange}
                  className="admin-input"
                  placeholder="Enter main about us title"
                />
                <small className="admin-form-text">
                  Main heading for your about us section
                </small>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Site Description</label>
                <textarea
                  name="description"
                  value={aboutUs.description}
                  onChange={handleChange}
                  className="admin-textarea"
                  rows="5"
                  placeholder="Enter your website's main description"
                />
                <small className="admin-form-text">
                  General description about your job portal
                </small>
              </div>
            </div>
          </div>

          {/* Services & Features Card */}
          <div className="admin-card" style={{ marginBottom: 'var(--spacing-xl)' }}>
            <div className="admin-card-header">
              <h3 className="admin-card-title">
                <FaBriefcase style={{ marginRight: '8px', color: 'var(--admin-success)' }} />
                Services & Features
              </h3>
            </div>
            <div className="admin-card-body">
              <div className="admin-form-group">
                <label className="admin-form-label">Featured Jobs Description</label>
                <textarea
                  name="featured_jobs"
                  value={aboutUs.featured_jobs}
                  onChange={handleChange}
                  className="admin-textarea"
                  rows="4"
                  placeholder="Describe your featured jobs offering"
                />
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">PSU Jobs Description</label>
                <textarea
                  name="psu_jobs"
                  value={aboutUs.psu_jobs}
                  onChange={handleChange}
                  className="admin-textarea"
                  rows="4"
                  placeholder="Describe PSU jobs on your platform"
                />
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Career Site Description</label>
                <textarea
                  name="career_site"
                  value={aboutUs.career_site}
                  onChange={handleChange}
                  className="admin-textarea"
                  rows="4"
                  placeholder="Describe career site features"
                />
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Third Party Listings Description</label>
                <textarea
                  name="third_party_listings"
                  value={aboutUs.third_party_listings}
                  onChange={handleChange}
                  className="admin-textarea"
                  rows="4"
                  placeholder="Describe third-party listings integration"
                />
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Sonia's Description</label>
                <textarea
                  name="sonia_description"
                  value={aboutUs.sonia_description}
                  onChange={handleChange}
                  className="admin-textarea"
                  rows="4"
                  placeholder="Enter Sonia's profile description"
                />
              </div>
            </div>
          </div>

          {/* Images Upload Card */}
          <div className="admin-card" style={{ marginBottom: 'var(--spacing-xl)' }}>
            <div className="admin-card-header">
              <h3 className="admin-card-title">
                <FaImage style={{ marginRight: '8px', color: 'var(--admin-warning)' }} />
                Images
              </h3>
            </div>
            <div className="admin-card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--spacing-xl)' }}>
                {/* About Us Image */}
                <div className="admin-form-group">
                  <label className="admin-form-label">About Us Image</label>
                  <div style={{ 
                    border: '2px dashed var(--admin-border)', 
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--spacing-lg)',
                    textAlign: 'center',
                    background: 'var(--admin-bg-secondary)'
                  }}>
                    {aboutUs.about_us_image && (
                      <div style={{ marginBottom: 'var(--spacing-md)' }}>
                        <img 
                          src={aboutUs.about_us_image} 
                          alt="About Us" 
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '200px',
                            borderRadius: 'var(--radius-md)',
                            objectFit: 'cover'
                          }} 
                        />
                      </div>
                    )}
                    <input
                      type="file"
                      onChange={(e) => handleImageUpload(e, "about_us_image")}
                      className="admin-input"
                      accept="image/*"
                      disabled={uploadingImage === "about_us_image"}
                    />
                    {uploadingImage === "about_us_image" && (
                      <small style={{ color: 'var(--admin-primary)', marginTop: '8px', display: 'block' }}>
                        Uploading...
                      </small>
                    )}
                  </div>
                </div>

                {/* Sonia Image */}
                <div className="admin-form-group">
                  <label className="admin-form-label">Sonia's Image</label>
                  <div style={{ 
                    border: '2px dashed var(--admin-border)', 
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--spacing-lg)',
                    textAlign: 'center',
                    background: 'var(--admin-bg-secondary)'
                  }}>
                    {aboutUs.sonia_image && (
                      <div style={{ marginBottom: 'var(--spacing-md)' }}>
                        <img 
                          src={aboutUs.sonia_image} 
                          alt="Sonia" 
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '200px',
                            borderRadius: 'var(--radius-md)',
                            objectFit: 'cover'
                          }} 
                        />
                      </div>
                    )}
                    <input
                      type="file"
                      onChange={(e) => handleImageUpload(e, "sonia_image")}
                      className="admin-input"
                      accept="image/*"
                      disabled={uploadingImage === "sonia_image"}
                    />
                    {uploadingImage === "sonia_image" && (
                      <small style={{ color: 'var(--admin-primary)', marginTop: '8px', display: 'block' }}>
                        Uploading...
                      </small>
                    )}
                  </div>
                </div>

                {/* Illustrate Image */}
                <div className="admin-form-group">
                  <label className="admin-form-label">Illustrate Image</label>
                  <div style={{ 
                    border: '2px dashed var(--admin-border)', 
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--spacing-lg)',
                    textAlign: 'center',
                    background: 'var(--admin-bg-secondary)'
                  }}>
                    {aboutUs.illustrate_image && (
                      <div style={{ marginBottom: 'var(--spacing-md)' }}>
                        <img 
                          src={aboutUs.illustrate_image} 
                          alt="Illustrate" 
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '200px',
                            borderRadius: 'var(--radius-md)',
                            objectFit: 'cover'
                          }} 
                        />
                      </div>
                    )}
                    <input
                      type="file"
                      onChange={(e) => handleImageUpload(e, "illustrate_image")}
                      className="admin-input"
                      accept="image/*"
                      disabled={uploadingImage === "illustrate_image"}
                    />
                    {uploadingImage === "illustrate_image" && (
                      <small style={{ color: 'var(--admin-primary)', marginTop: '8px', display: 'block' }}>
                        Uploading...
                      </small>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div style={{ display: 'flex', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-xl)' }}>
            <button 
              type="submit" 
              className="admin-btn admin-btn-primary"
              disabled={saving}
            >
              <FaSave />
              {saving ? 'Saving Changes...' : 'Save All Changes'}
            </button>
          </div>
        </form>
      )}

      {/* Team Management Section */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">
            <FaUser style={{ marginRight: '8px', color: 'var(--admin-info)' }} />
            Team Management
          </h3>
        </div>
        <div className="admin-card-body">
          <TeamEdit />
        </div>
      </div>
    </AdminLayout>
  );
};

export default AboutUsEdit;
